import * as React from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';

import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import Button from '../button';
import StyledContainer from './components/styledContainer';
import Popup from '../popup';

import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';
import {
    codeAPI,
    userAPI,
    authorizeAPI,
    membershipAPI,
    settingAPI,
} from '../../actions/imsServer';
import { useInterval } from '../../actions/interval';
import * as jwt from '../../actions/jwt';
import { useDispatch } from 'react-redux';
import InputField from '../formItem/inputField';
import Checkbox from '../checkbox';
import Toggle from '../toggle';
import SocialLoginProvider from './components/socialLoginProvider';
import SignUpLink from './components/signUpLink';
import Navigation from '../navigation';
import { isWebView, formatDateTimeDisplay, getBirthDisplayValue, parseLocaleDateToYyyymmdd } from '../../utils/base';
import FormLabel from '../formItem/formLabel';
import { isEmail, isValidPassword } from '../../utils/password-validation';

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.figma.white[60],
    fontSize: '14px',
    fontWeight: '400',
}));

const StyledLinkTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.figma.Red_didim,
    fontSize: '14px',
    fontWeight: '500',
    textDecoration: 'underline',
    cursor: 'pointer',
}));

const SNS_SIGN_UP_STORAGE_KEY = 'snsData';
const SNS_SIGN_UP_STORAGE_EVENT = 'snsDataChanged';

const hasStoredValue = value =>
    value !== undefined && value !== null && value !== '';

const getStoredSnsData = () => {
    try {
        const stored = localStorage.getItem(SNS_SIGN_UP_STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        return null;
    }
};

const setStoredSnsData = snsData => {
    if (!snsData) return;

    try {
        const storedSnsData = getStoredSnsData();
        const nextSnsData = isSameSnsAccount(storedSnsData, snsData)
            ? mergeSnsData(storedSnsData, snsData)
            : snsData;

        localStorage.setItem(SNS_SIGN_UP_STORAGE_KEY, JSON.stringify(nextSnsData));
    } catch (error) {
        // localStorage 저장 실패 시에도 가입 흐름은 계속 진행합니다.
    }
};

const removeStoredSnsData = () => {
    try {
        localStorage.removeItem(SNS_SIGN_UP_STORAGE_KEY);
    } catch (error) {
        // localStorage 접근이 실패해도 화면 동작은 유지합니다.
    }
};

const isSameSnsAccount = (a, b) =>
    !!a?.snsType &&
    !!a?.snsId &&
    a.snsType === b?.snsType &&
    a.snsId === b?.snsId;

const mergeSnsData = (previous, next) => {
    if (!previous) return next || null;
    if (!next) return previous;

    return Array.from(new Set([...Object.keys(previous), ...Object.keys(next)]))
        .reduce((merged, key) => ({
            ...merged,
            [key]: hasStoredValue(next[key]) ? next[key] : previous[key],
        }), {});
};

const getSnsDataForSignUp = (snsData, useStoredSnsData = false) => {
    if (!snsData) return useStoredSnsData ? getStoredSnsData() : null;

    const storedSnsData = getStoredSnsData();
    if (!isSameSnsAccount(storedSnsData, snsData)) {
        return snsData;
    }

    return mergeSnsData(storedSnsData, snsData);
};

export const SignUp = props => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme();
    const incomingSnsData = location?.state?.snsData || location?.state?.formData?.snsData;
    const shouldUseStoredSnsData = location?.state?.step === 1 || !!incomingSnsData;
    const [snsDataForSignUp, setSnsDataForSignUp] = React.useState(
        () => getSnsDataForSignUp(incomingSnsData, shouldUseStoredSnsData)
    );
    const isSnsSignUpForRender = !!snsDataForSignUp;

    const [email, setEmail] = React.useState(snsDataForSignUp?.email || '');
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [auth, setAuth] = React.useState('');
    const [agree, setAgree] = React.useState(false);
    // 이메일 가입 중 '이미 존재하는 SNS 계정'을 발견했을 때 비밀번호 설정 플로우를 위한 플래그
    const [existingSnsAccount, setExistingSnsAccount] = React.useState(false);
    // 기존 SNS 계정의 식별값/프로필(입력 UI 없이 request에 재사용)
    const [existingSnsType, setExistingSnsType] = React.useState(null);
    const [existingSnsId, setExistingSnsId] = React.useState(null);
    const [existingProfile, setExistingProfile] = React.useState(null);
    const [password, setPassword] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [passwordConfirm, setPasswordConfirm] = React.useState('');
    const [passwordConfirmError, setPasswordConfirmError] = React.useState(false);
    const [passwordConfirmErrorMessage, setPasswordConfirmErrorMessage] = React.useState('');
    const [userName, setUserName] = React.useState(snsDataForSignUp?.name || '');
    const [userNameError, setUserNameError] = React.useState(false);
    const [userNameErrorMessage, setUserNameErrorMessage] = React.useState('');
    const [step, setStep] = React.useState(location?.state?.step || 0);

    const type = [
        'terms_of_service',
        'privacy_policy'
    ];

    React.useEffect(() => {
        const handleSnsDataChanged = event => {
            setSnsDataForSignUp(event.detail || getStoredSnsData());
        };

        window.addEventListener(SNS_SIGN_UP_STORAGE_EVENT, handleSnsDataChanged);
        return () => {
            window.removeEventListener(SNS_SIGN_UP_STORAGE_EVENT, handleSnsDataChanged);
        };
    }, []);

    React.useEffect(() => {
        const nextSnsData = getSnsDataForSignUp(incomingSnsData, shouldUseStoredSnsData);
        setSnsDataForSignUp(nextSnsData);
    }, [incomingSnsData, shouldUseStoredSnsData]);

    React.useEffect(() => {
        if (!isSnsSignUpForRender) return;

        setStoredSnsData(snsDataForSignUp);
        if (snsDataForSignUp?.email) setEmail(snsDataForSignUp.email);
        if (snsDataForSignUp?.name) setUserName(snsDataForSignUp.name);
    }, [isSnsSignUpForRender, snsDataForSignUp]);

    // SNS로 가입된 이메일로 "재가입"하는 경우: 비밀번호/비밀번호 확인만 받음
    // 주의: 인증번호 전송(step=0) 화면에서는 이메일 입력이 사라지면 안 되므로 step===1일 때만 적용
    const isPasswordOnlyFlow = step === 1 && existingSnsAccount && !isSnsSignUpForRender;

    // location.state에서 step이 변경될 때 step 상태 업데이트
    React.useEffect(() => {
        if (location?.state?.step !== undefined) {
            setStep(location.state.step);
        }
    }, [location?.state?.step]);

    // Step 1 추가 필드들
    const [birthDate, setBirthDate] = React.useState('');
    const [birthDateError, setBirthDateError] = React.useState(false);
    const [birthDateErrorMessage, setBirthDateErrorMessage] = React.useState('');
    /** 포커스 중에는 yyyymmdd 숫자만 표시 — 로케일 포맷 상태에서 백스페이스가 먹지 않는 문제 방지 */
    const [birthDateFocused, setBirthDateFocused] = React.useState(false);
    const [gender, setGender] = React.useState(0);
    const [organizationId, setOrganizationId] = React.useState(null);
    const [selectedOrganization, setSelectedOrganization] = React.useState(
        location?.state?.selectedOrganization || null
    );
    const [division, setDivision] = React.useState(
        location?.state?.selectedDivision || ''
    );
    const [section, setSection] = React.useState(
        location?.state?.selectedSection || ''
    );

    // location.state에서 formData 복원
    React.useEffect(() => {
        const formData = location?.state?.formData;

        if (formData) {
            // 각 필드 복원
            if (formData.email) {
                setEmail(formData.email);
                // 유효성 검사
                if (!/\S+@\S+\.\S+/.test(formData.email)) {
                    setEmailError(true);
                    setEmailErrorMessage(t('please_enter_a_valid_email_address'));
                } else {
                    setEmailError(false);
                    setEmailErrorMessage('');
                }
            }

            if (formData.auth) setAuth(formData.auth);

            // formData에 비밀번호가 있으면 항상 복원 (입력한 값 보존)
            if (formData.password) {
                setPassword(formData.password);
                // 유효성 검사
                if (!formData.password || !isValidPassword(formData.password)) {
                    setPasswordError(true);
                    setPasswordErrorMessage(t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters'));
                } else {
                    setPasswordError(false);
                    setPasswordErrorMessage('');
                }
            }

            if (formData.passwordConfirm) {
                setPasswordConfirm(formData.passwordConfirm);
                // 유효성 검사
                if (formData.password) {
                    if (formData.passwordConfirm !== formData.password) {
                        setPasswordConfirmError(true);
                        setPasswordConfirmErrorMessage(t('password_mismatch'));
                    } else {
                        setPasswordConfirmError(false);
                        setPasswordConfirmErrorMessage('');
                    }
                }
            }

            if (formData.userName) {
                setUserName(formData.userName);
                // 유효성 검사
                if (!formData.userName || formData.userName.length < 2) {
                    setUserNameError(true);
                    setUserNameErrorMessage(t('name_must_be_at_least_2_characters_long'));
                } else {
                    setUserNameError(false);
                    setUserNameErrorMessage('');
                }
            }

            if (formData.birthDate) {
                setBirthDate(formData.birthDate);
                // 유효성 검사
                if (!formData.birthDate || formData.birthDate.length !== 8) {
                    setBirthDateError(true);
                    setBirthDateErrorMessage(t('please_enter_your_date_of_birth_8_digits'));
                } else {
                    setBirthDateError(false);
                    setBirthDateErrorMessage('');
                }
            }

            if (formData.gender !== undefined) setGender(formData.gender);
            if (formData.step !== undefined) setStep(formData.step);
            if (formData.agree !== undefined) setAgree(formData.agree);
            if (formData.organizationId !== undefined) setOrganizationId(formData.organizationId);
            if (formData.selectedOrganization) setSelectedOrganization(formData.selectedOrganization);
            if (formData.division) setDivision(formData.division);
            if (formData.section) setSection(formData.section);
            if (formData.existingSnsAccount !== undefined) setExistingSnsAccount(formData.existingSnsAccount);
            if (formData.existingSnsType !== undefined) setExistingSnsType(formData.existingSnsType);
            if (formData.existingSnsId !== undefined) setExistingSnsId(formData.existingSnsId);
            if (formData.existingProfile !== undefined) setExistingProfile(formData.existingProfile);
        }
    }, [location?.state?.formData, location?.state?.snsData, t]);

    // SNS 가입 화면에서는 비밀번호 필드를 사용하지 않으므로 값/에러를 제거합니다.
    React.useEffect(() => {
        if (!isSnsSignUpForRender) return;

        setPassword('');
        setPasswordConfirm('');
        setPasswordError(false);
        setPasswordConfirmError(false);
        setPasswordErrorMessage('');
        setPasswordConfirmErrorMessage('');
    }, [isSnsSignUpForRender]);

    // 팝업 상태
    const [popupOpen, setPopupOpen] = React.useState(false);
    const [popupMessage, setPopupMessage] = React.useState('');
    const [popupSubMessage, setPopupSubMessage] = React.useState('');

    const [countdown, setCountdown] = React.useState(0);
    const [hasSentCode, setHasSentCode] = React.useState(false);

    // 팝업 표시 함수
    const showPopup = (message, subMessage = '') => {
        setPopupMessage(message);
        setPopupSubMessage(subMessage);
        setPopupOpen(true);
    };

    // 팝업 닫기 함수
    const handleClosePopup = async () => {
        setPopupOpen(false);
    };

    // 카운트다운 interval (useInterval 사용)
    useInterval(() => {
        if (countdown > 0) {
            setCountdown(prev => prev - 1);
        }
    }, countdown > 0 ? 1000 : null);

    // 선택된 소속기관이 있을 때 코드 설정
    React.useEffect(() => {
        if (selectedOrganization) {
            setOrganizationId(
                selectedOrganization.id
            );
        }
    }, [selectedOrganization]);

    // 플랫폼 정보 가져오기
    React.useEffect(() => {
        const originalConsoleLog = console.log;
        console.log = (...args) => {
            originalConsoleLog.apply(console, args);
            if (isWebView()) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'console',
                        data: args,
                    })
                );
            }
        };

        // Cleanup
        return () => {
            console.log = originalConsoleLog;
        };
    }, []);

    const handleSubmit = event => {
        if (userNameError || emailError || passwordError) {
            event.preventDefault();
            return;
        }
    };

    const handleEmailChange = event => {
        const value = event.target.value;
        setEmail(value);
        if (!value || !isEmail(value)) {
            setEmailError(true);
            setEmailErrorMessage(t('please_enter_a_valid_email_address'));
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }
    };

    const handleClearEmail = () => setEmail('');

    const handleAuthChange = event => {
        const value = event.target.value;
        setAuth(value);
    };

    const handleAuthEmail = async () => {
        // 버튼을 누르자마자 비활성화
        setCountdown(60);
        setHasSentCode(true);

        try {
            let target = email;

            const checkResponse = await userAPI.checkUserExists({
                loginId: target,
            });
            if (checkResponse.data.exists === true) {
                // smart-diaper-admin 로직:
                // exists=true 이면서 isValid=true 이면 "이미 존재하는 아이디"로 막고,
                // exists=true 이면서 isValid=false(=SNS 계정 등) 이면 인증번호 발송 진행
                const data = checkResponse.data || {};
                const isValid = data.isValid;

                const profile = data.user || data;

                // isValid 응답이 없을 경우(구버전 API 등) fallback으로 SNS 유사 여부를 추정
                const snsType =
                    data.snsType ||
                    data.sns_type ||
                    data?.user?.snsType ||
                    data?.user?.sns_type;
                const snsId =
                    data.snsId ||
                    data.sns_id ||
                    data?.user?.snsId ||
                    data?.user?.sns_id;
                const looksLikeSnsAccount =
                    Boolean(snsType || snsId) ||
                    data?.isSns === true ||
                    data?.sns === true;

                // 일반(비밀번호 이미 있는) 계정이면 차단
                if (isValid === true) {
                    setExistingSnsAccount(false);
                    setExistingSnsType(null);
                    setExistingSnsId(null);
                    setExistingProfile(null);
                    showPopup(t('already_exists'));
                    setCountdown(0);
                    setHasSentCode(false);
                    return;
                }

                // SNS(비밀번호 없는) 계정이면 인증번호 발송
                // isValid=false가 오면 무조건 진행, isValid가 undefined면 fallback SNS 유사 여부로 판단
                const shouldSendCode =
                    isValid === false ? true : looksLikeSnsAccount;

                if (!shouldSendCode) {
                    setExistingSnsAccount(false);
                    setExistingSnsType(null);
                    setExistingSnsId(null);
                    setExistingProfile(null);
                    showPopup(t('already_exists'));
                    setCountdown(0);
                    setHasSentCode(false);
                    return;
                }

                setExistingSnsAccount(true);
                setExistingSnsType(snsType || null);
                setExistingSnsId(snsId || null);
                setExistingProfile(profile || null);

                const codeType = 0;
                await codeAPI.generateCode({ target: target, type: codeType });
                showPopup(`${t('verification_code_sent')} ${email}`);
                return;
            }

            setExistingSnsAccount(false);
            setExistingSnsType(null);
            setExistingSnsId(null);
            setExistingProfile(null);

            const codeType = 0;
            await codeAPI.generateCode({ target: target, type: codeType });
            showPopup(`${t('verification_code_sent')} ${email}`);
            // 성공 시에는 이미 설정된 상태 유지
        } catch (error) {
            console.error('인증번호 발송 실패:', error);

            // 에러 발생 시 버튼 상태 복원
            setCountdown(0);
            setHasSentCode(false);

            // 에러 메시지가 'code_limit_exceeded_per_day'인 경우 특정 메시지 표시
            if (error?.message === 'code_limit_exceeded_per_day') {
                showPopup(t('the_number_of_attempts_has_been_exceeded_please_try_again_tomorrow'));
            } else if (error.message.indexOf('code 400') > -1) {
                showPopup(t('please_enter_a_valid_email_address'));
            } else {
                showPopup(t('an_error_occurred'));
            }
        }
    };

    const handleNext = async () => {
        try {
            let target = email;

            const codeType = 0;
            const response = await codeAPI.confirmCode({
                target: target,
                type: codeType,
                key: auth,
            });
            if (response.result === 'ok') {
                setStep(1);
            }
        } catch (error) {
            showPopup(t('invalid_verification_code'));
        }
    };

    const handleClearPassword = () => setPassword('');
    const handleClearPasswordConfirm = () => setPasswordConfirm('');

    const handlePasswordChange = event => {
        const value = event.target.value;
        setPassword(value);

        if (!value || !isValidPassword(value)) {
            setPasswordError(true);
            setPasswordErrorMessage(t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters'));
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }
        // 비밀번호와 비밀번호 확인이 모두 입력되어 있으면 비교
        if (passwordConfirm) {
            if (value !== passwordConfirm) {
                setPasswordConfirmError(true);
                setPasswordConfirmErrorMessage(t('password_mismatch'));
            } else {
                setPasswordConfirmError(false);
                setPasswordConfirmErrorMessage('');
            }
        }
    };

    const handlePasswordConfirmChange = event => {
        const value = event.target.value;
        setPasswordConfirm(value);
        // 비밀번호 확인 입력값 자체가 너무 짧으면 일단 기본에러(선택)
        if (!value || value.length < 4) {
            setPasswordConfirmError(true);
            setPasswordConfirmErrorMessage(t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters'));
        } else if (password) {
            if (password !== value) {
                setPasswordConfirmError(true);
                setPasswordConfirmErrorMessage(t('password_mismatch'));
            } else {
                setPasswordConfirmError(false);
                setPasswordConfirmErrorMessage('');
            }
        } else {
            setPasswordConfirmError(false);
            setPasswordConfirmErrorMessage('');
        }
    };

    const handleClearUserName = () => setUserName('');

    const handleUserNameChange = event => {
        const value = event.target.value;
        setUserName(value);

        if (!value || value.length < 2) {
            setUserNameError(true);
            setUserNameErrorMessage(t('name_must_be_at_least_2_characters_long'));
        } else {
            setUserNameError(false);
            setUserNameErrorMessage('');
        }
    };

    // Step 1 핸들러 함수들 (생년월일: 로케일별 형식 입력 → yyyymmdd로 저장)
    const handleBirthDateChange = event => {
        const value = event.target.value;
        const digitsOnly = value.replace(/[^\d]/g, '').slice(0, 8);
        const currentDigits = String(birthDate ?? '').replace(/[^\d]/g, '');
        if (!value.trim()) {
            setBirthDate('');
            setBirthDateError(true);
            setBirthDateErrorMessage(t('please_enter_your_date_of_birth_8_digits'));
            return;
        }
        // 삭제/백스페이스: 포맷된 표시(MM/DD/YYYY 등)에서 뽑은 숫자 순서는 yyyymmdd와 다를 수 있음 → 저장값 끝에서 제거
        if (digitsOnly.length < currentDigits.length && currentDigits.length > 0) {
            let next;
            if (digitsOnly === currentDigits.slice(0, digitsOnly.length)) {
                next = digitsOnly;
            } else {
                const removed = currentDigits.length - digitsOnly.length;
                next =
                    removed > 0
                        ? currentDigits.slice(0, currentDigits.length - removed)
                        : digitsOnly;
            }
            setBirthDate(next);
            if (next.length !== 8) {
                setBirthDateError(true);
                setBirthDateErrorMessage(t('please_enter_your_date_of_birth_8_digits'));
            } else {
                setBirthDateError(false);
                setBirthDateErrorMessage('');
            }
            return;
        }
        const parsed = parseLocaleDateToYyyymmdd(value, i18n.language);
        if (parsed) {
            setBirthDate(parsed);
            setBirthDateError(false);
            setBirthDateErrorMessage('');
            return;
        }
        setBirthDate(digitsOnly);
        if (digitsOnly.length !== 8) {
            setBirthDateError(true);
            setBirthDateErrorMessage(t('please_enter_your_date_of_birth_8_digits'));
        } else {
            setBirthDateError(false);
            setBirthDateErrorMessage('');
        }
    };

    const handleGenderChange = index => {
        setGender(index);
    };

    const buildTermsNavigationState = termType => {
        const snsData =
            snsDataForSignUp ||
            location?.state?.snsData ||
            location?.state?.formData?.snsData;
        // iOS WebView 안정성을 위해 history state payload를 최소화합니다.
        const safeFormData = {
            email,
            auth,
            password,
            passwordConfirm,
            userName,
            birthDate,
            gender,
            step,
            agree,
            organizationId,
            division,
            section,
            existingSnsAccount,
            existingSnsType,
            existingSnsId,
        };

        // 객체가 큰 selectedOrganization/existingProfile은 필요한 값만 축약 전달
        if (selectedOrganization?.id || selectedOrganization?.name) {
            safeFormData.selectedOrganization = {
                id: selectedOrganization?.id,
                name: selectedOrganization?.name,
            };
        }
        if (snsData?.snsType || snsData?.snsId || snsData?.email || snsData?.name) {
            safeFormData.snsData = {
                snsType: snsData?.snsType,
                snsId: snsData?.snsId,
                email: snsData?.email,
                name: snsData?.name,
            };
        }
        if (existingProfile) {
            safeFormData.existingProfile = {
                name: existingProfile?.name,
                birth: existingProfile?.birth,
                gender: existingProfile?.gender,
                mainOrganizationId:
                    existingProfile?.mainOrganizationId ?? existingProfile?.organizationId,
            };
        }

        return {
            type: termType,
            source: '/auth/signUp',
            step,
            formData: safeFormData,
        };
    };

    const handleRegister = async () => {
        try {
            // SNS 로그인을 통한 회원가입인지 확인
            const snsData = snsDataForSignUp;
            const isSnsSignUp = !!snsData;

            // SNS 이메일 계정이 이미 존재하는 경우(기존 계정에 비밀번호만 추가/설정)
            // resetPassword 대신 registerUser로 처리합니다.
            const isPasswordOnlyAccount = !isSnsSignUp && existingSnsAccount;

            // (안전장치) terms를 거치거나 state가 비어있을 수 있으므로, 필요하면 다시 조회해 request에 쓸 값을 채웁니다.
            let profileForRequest = existingProfile;
            let snsTypeForRequest = existingSnsType;
            let snsIdForRequest = existingSnsId;
            if (isPasswordOnlyAccount && (!profileForRequest || (!snsTypeForRequest && !snsIdForRequest))) {
                const check = await userAPI.checkUserExists({ loginId: email });
                const data = check?.data || {};
                profileForRequest = data.user || data || null;

                snsTypeForRequest =
                    data.snsType ||
                    data.sns_type ||
                    data?.user?.snsType ||
                    data?.user?.sns_type ||
                    null;
                snsIdForRequest =
                    data.snsId ||
                    data.sns_id ||
                    data?.user?.snsId ||
                    data?.user?.sns_id ||
                    null;
            }

            // registerUser 요청 데이터 구성 (SNS 재가입 케이스면 기존 프로필을 그대로 사용)
            const birthParsed = parseLocaleDateToYyyymmdd(birthDate, i18n.language);
            const birthYyyymmdd =
                birthParsed ||
                String(birthDate ?? '')
                    .replace(/[^\d]/g, '')
                    .slice(0, 8);

            const registerBirth = profileForRequest?.birth
                ? profileForRequest.birth
                : birthYyyymmdd.length === 8
                    ? birthYyyymmdd
                    : birthDate;

            const registerData = {
                name: profileForRequest?.name ?? userName,
                birth: registerBirth,
                gender: profileForRequest?.gender ?? gender,
                mainOrganizationId:
                    profileForRequest?.mainOrganizationId ??
                    profileForRequest?.organizationId ??
                    organizationId,
            };

            // SNS 회원가입인 경우 snsType, snsId 사용
            if (isSnsSignUp && snsData) {
                registerData.snsType = snsData.snsType;
                registerData.snsId = snsData.snsId;
                if (snsData.email && snsData.email.length > 0) {
                    registerData.loginId = snsData.email;
                    registerData.email = snsData.email;
                }
                // snsData.email이 없어도 SNS 가입으로 처리
            } else {
                // 일반 회원가입인 경우 loginId, loginPwd 사용
                registerData.loginId = email;
                registerData.loginPwd = password;
                registerData.email = email;

                // SNS 이메일로 기존 계정 재가입(비밀번호만 설정)
                if (isPasswordOnlyAccount) {
                    if (snsTypeForRequest) registerData.snsType = snsTypeForRequest;
                    if (snsIdForRequest) registerData.snsId = snsIdForRequest;
                }
            }

            const response = await userAPI.registerUser(registerData);

            if (response.result === 'created' || response.result === 'ok') {
                // response.result === 'created'인 경우 즉시 자동 로그인
                // 회원가입 성공 후 자동 로그인
                let loginResponse;

                if (isSnsSignUp && snsData) {
                    // SNS 로그인
                    const pushToken = localStorage.getItem('pushToken') || '';
                    loginResponse = await authorizeAPI.getToken({
                        grant_type: 'sns',
                        sns_type: snsData.snsType,
                        sns_id: snsData.snsId,
                        sns_code: snsData.snsCode || null,
                        push_token: pushToken,
                    });
                } else {
                    // 일반 로그인
                    loginResponse = await authorizeAPI.getToken({
                        grant_type: 'password',
                        username: email,
                        password: password,
                    });
                }

                if (loginResponse.access_token) {
                    const appType = localStorage.getItem('appType');
                    const decoded = jwt.decode(loginResponse.access_token);

                    if (appType === 'admin' && decoded.exp) {
                        showPopup(t('admin_only_access'));
                    } else {
                        localStorage.setItem(
                            'accessToken',
                            loginResponse.access_token
                        );
                        localStorage.setItem(
                            'refreshToken',
                            loginResponse.refresh_token
                        );

                        dispatch({
                            type: 'info/setAuth',
                            payload: {
                                accessToken: loginResponse.access_token,
                                refreshToken: loginResponse.refresh_token,
                            },
                        });

                        if (organizationId && !isPasswordOnlyAccount) {
                            const membershipResponse = await membershipAPI.registerMembership({
                                userId: response.data.user.id,
                                organizationId: organizationId,
                                division: division,
                                section: section,
                                type: 1,
                                state: 0
                            });

                            if (membershipResponse.result !== 'created') {
                                await userAPI.updateUser({
                                    userId: response.data.user.id,
                                    mainOrganizationId: 0
                                });
                            }
                        }

                        const userResponse = await userAPI.getMyInfo(
                            loginResponse.access_token
                        );
                        localStorage.setItem(
                            'meInfo',
                            JSON.stringify(userResponse.data.user)
                        );
                        removeStoredSnsData();

                        // 가입 시 현재 앱 언어를 DB에 저장
                        try {
                            await settingAPI.registerSetting({ name: 'language', value: i18n.language });
                        } catch {
                            // 언어 설정 저장 실패해도 가입 흐름은 계속
                        }

                        // meInfo 변경 이벤트 발생 (App.js에서 meInfo 업데이트를 위해)
                        window.dispatchEvent(new Event('meInfoChanged'));

                        if (isWebView()) {
                            window.ReactNativeWebView.postMessage(
                                JSON.stringify({
                                    type: 'accessToken',
                                    data: {
                                        accessToken: loginResponse.access_token,
                                        refreshToken: loginResponse.refresh_token,
                                    },
                                })
                            );

                            // meInfo도 WebView로 전달
                            window.ReactNativeWebView.postMessage(
                                JSON.stringify({
                                    type: 'meInfo',
                                    data: userResponse.data.user,
                                })
                            );
                        }

                        if (isPasswordOnlyAccount) {
                            setExistingSnsAccount(false);
                            setExistingSnsType(null);
                            setExistingSnsId(null);
                            setExistingProfile(null);
                        }

                        //navigate(`/?appType=${appType}`, { replace: true, state: { redirectToFace: true } });
                        navigate(`/?appType=${appType}`, { replace: true });
                    }
                }
            }
        } catch (error) {
            // WebView로 로그 전달
            if (isWebView()) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'console',
                        data: ['[SignUp] handleRegister error:', error]
                    })
                );

                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'console',
                        data: ['[SignUp] error.response:', error.response.data]
                    })
                );
            }

            if (error.response?.status === 409) {
                if (error.response?.data?.message === 'already_exists') {
                    showPopup(t('already_exists'));
                }
                else if (error.response?.data?.message === 'already_exists_manager') {
                    showPopup(t('you_are_an_administrator_please_use_the_web_page'));
                }
            } else {
                showPopup(t('sign_up_failed'));
            }

            setStep(0);
            setAuth('');
            setPassword('');
            setPasswordConfirm('');
            setUserName('');
            setExistingSnsAccount(false);
            setExistingSnsType(null);
            setExistingSnsId(null);
            setExistingProfile(null);
        }
    };

    const commonLabelContainerSx = {
        minWidth: '100px',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        height: '40px',
        flexShrink: 0,
    };

    const commonLabelTextSx = {
        whiteSpace: 'nowrap',
        textAlign: 'left',
    };

    return (
        <StyledContainer>
            <Navigation title={t('sign_up')} />
            {step === 0 ? (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            padding: '64px 0px 32px 0px',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '50px',
                            flex: '1 0 0',
                            alignSelf: 'stretch',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '40px',
                                alignSelf: 'stretch',
                            }}
                        >
                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: '20px',
                                    alignSelf: 'stretch',
                                }}
                            >
                                <FormControl
                                    sx={{
                                        display: isPasswordOnlyFlow ? 'none' : 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '10px',
                                        alignSelf: 'stretch',
                                    }}
                                >
                                    <FormLabel text={t('e_mail')} />

                                    <InputField
                                        required
                                        fullWidth
                                        id="email"
                                        placeholder={t(
                                            'enter_email'
                                        )}
                                        name="email"
                                        variant="outlined"
                                        error={emailError}
                                        helperText={emailErrorMessage || ' '}
                                        value={email}
                                        onChange={handleEmailChange}
                                        onClear={handleClearEmail}
                                        showClearIcon={true}
                                    />
                                </FormControl>
                                <FormControl
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '10px',
                                        alignSelf: 'stretch',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '10px',
                                            alignSelf: 'stretch',
                                        }}
                                    >
                                        <FormLabel text={t('verification_code')} />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                width: '100%',
                                                maxWidth: { md: 420 },
                                                alignSelf: { md: 'center' },
                                                mx: { md: 'auto' },
                                                height: { xs: '40px', md: '48px' },
                                                alignItems: 'stretch',
                                                gap: '10px',
                                            }}
                                        >
                                            <InputField
                                                required
                                                fullWidth
                                                id="auth"
                                                placeholder={t(
                                                    'verification_number'
                                                )}
                                                name="auth"
                                                variant="outlined"
                                                value={auth}
                                                onChange={handleAuthChange}
                                                showClearIcon={false}
                                                type="password"
                                                showVisibilityIcon={true}
                                                sx={{
                                                    flex: '1 1 0',
                                                    maxWidth: 'none',
                                                    alignSelf: 'stretch',
                                                    mx: 0,
                                                    height: { xs: '40px', md: '48px' },
                                                    minHeight: { xs: '40px', md: '48px' },
                                                }}
                                            />
                                            <Button
                                                variant="normal"
                                                disabled={
                                                    countdown > 0 ||
                                                    emailError ||
                                                    !email
                                                }
                                                onClick={handleAuthEmail}
                                                sx={{
                                                    height: { xs: '40px', md: '48px' },
                                                    minHeight: { xs: '40px', md: '48px' },
                                                    maxHeight: { xs: '40px', md: '48px' },
                                                    alignSelf: 'stretch',
                                                    boxSizing: 'border-box',
                                                    py: 0,
                                                }}
                                            >
                                                {countdown > 0
                                                    ? `${t('resend')}(${countdown}s)`
                                                    : hasSentCode
                                                        ? t(
                                                            'resend_code'
                                                        )
                                                        : t(
                                                            'send_code'
                                                        )}
                                            </Button>
                                        </Box>
                                    </Box>
                                </FormControl>
                            </Box>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '40px',
                            alignSelf: 'stretch',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                padding: '0px 0px',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '20px',
                                alignSelf: 'stretch',
                            }}
                        >
                            <Button
                                size={'large'}
                                disabled={!auth}
                                onClick={handleNext}
                                sx={{
                                    alignSelf: 'stretch',
                                    height: '60px',
                                    minHeight: '60px',
                                    maxHeight: '60px',
                                }}
                            >
                                {t('next')}
                            </Button>
                            <SocialLoginProvider />
                            <SignUpLink disabled />
                        </Box>
                    </Box>
                </>
            ) : (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            padding: '40px 0px 32px 0px',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '50px',
                            flex: '1 0 0',
                            alignSelf: 'stretch',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '40px',
                                alignSelf: 'stretch',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: '20px',
                                    alignSelf: 'stretch',
                                }}
                            >
                                {/* 계정 정보 섹션 */}
                                {email && <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '0px',
                                        alignSelf: 'stretch',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '10px',
                                            alignSelf: 'stretch',
                                            padding: '10px 0px',
                                        }}
                                    >
                                        <StyledTypography
                                            sx={{
                                                textAlign: 'center',
                                                fontSize: '14px',
                                                fontWeight: '400',
                                            }}
                                        >
                                            {t('account')}
                                        </StyledTypography>
                                        <Typography
                                            sx={{
                                                textAlign: 'center',
                                                fontSize: '16px',
                                                fontWeight: '500',
                                            }}
                                        >
                                            {email}
                                        </Typography>
                                    </Box>

                                    {/* 비밀번호 섹션: SNS 가입인 경우 비밀번호 없이 가입 */}
                                    {!isSnsSignUpForRender && email && <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '0px',
                                            alignSelf: 'stretch',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '0px',
                                                alignSelf: 'stretch',
                                            }}
                                        >
                                            <InputField
                                                required
                                                fullWidth
                                                id="password"
                                                placeholder={t('password')}
                                                type="password"
                                                name="password"
                                                variant="outlined"
                                                value={password}
                                                onChange={handlePasswordChange}
                                                error={passwordError}
                                                helperText={
                                                    passwordErrorMessage || ' '
                                                }
                                                onClear={handleClearPassword}
                                                showClearIcon={true}
                                                showVisibilityIcon={true}
                                            />
                                            <InputField
                                                required
                                                fullWidth
                                                id="passwordConfirm"
                                                placeholder={t('confirm_password')}
                                                type="password"
                                                name="passwordConfirm"
                                                value={passwordConfirm}
                                                onChange={
                                                    handlePasswordConfirmChange
                                                }
                                                error={passwordConfirmError}
                                                helperText={
                                                    passwordConfirmErrorMessage || ' '
                                                }
                                                variant="outlined"
                                                onClear={
                                                    handleClearPasswordConfirm
                                                }
                                                showClearIcon={true}
                                                showVisibilityIcon={true}
                                            />
                                            <Typography
                                                sx={{
                                                    color: theme.palette.figma
                                                        .white[60],
                                                    fontSize: '12px',
                                                    fontWeight: 400,
                                                    lineHeight: 'normal',
                                                    letterSpacing: '-0.36px',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters')}
                                            </Typography>
                                        </Box>
                                    </Box>}
                                </Box>}

                                {/* 개인정보 섹션 */}
                                <Box
                                    sx={{
                                        display: isPasswordOnlyFlow ? 'none' : 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: '0px',
                                        alignSelf: 'stretch',
                                    }}
                                >
                                    {/* 이름 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '10px',
                                            alignSelf: 'stretch',
                                            paddingLeft: '15px',
                                        }}
                                    >
                                        <Box
                                            sx={commonLabelContainerSx}
                                        >
                                            <FormLabel text={t('name')} required sx={commonLabelTextSx} />
                                        </Box>
                                        <InputField
                                            required
                                            fullWidth
                                            id="userName"
                                            placeholder={t('enter_name')}
                                            name="userName"
                                            variant="outlined"
                                            value={userName}
                                            onChange={handleUserNameChange}
                                            error={userNameError}
                                            helperText={userNameErrorMessage || ' '}
                                            onClear={handleClearUserName}
                                            showClearIcon={true}
                                        />
                                    </Box>

                                    {/* 생년월일 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '10px',
                                            alignSelf: 'stretch',
                                            paddingLeft: '15px',
                                        }}
                                    >
                                        <Box
                                            sx={commonLabelContainerSx}
                                        >
                                            <FormLabel
                                                text={t('birth_date')}
                                                required
                                                sx={commonLabelTextSx}
                                            />
                                        </Box>
                                        <InputField
                                            required
                                            fullWidth
                                            id="birthDate"
                                            placeholder={formatDateTimeDisplay('1990-03-15', 'date')}
                                            name="birthDate"
                                            variant="outlined"
                                            value={
                                                birthDateFocused
                                                    ? String(birthDate ?? '')
                                                        .replace(/[^\d]/g, '')
                                                        .slice(0, 8)
                                                    : getBirthDisplayValue(birthDate)
                                            }
                                            onChange={handleBirthDateChange}
                                            onFocus={() => setBirthDateFocused(true)}
                                            onBlur={() => setBirthDateFocused(false)}
                                            inputProps={{
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                                maxLength: birthDateFocused ? 8 : 14,
                                            }}
                                            error={birthDateError}
                                            helperText={birthDateErrorMessage || ' '}
                                            showClearIcon={false}
                                        />
                                    </Box>

                                    {/* 성별 토글 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '10px',
                                            alignSelf: 'stretch',
                                            paddingLeft: '15px',
                                            paddingBottom: '10px',
                                        }}
                                    >
                                        <Box
                                            sx={commonLabelContainerSx}
                                        >
                                            <FormLabel text={t('gender')} required sx={commonLabelTextSx} />
                                        </Box>
                                        <Toggle
                                            options={[t('male'), t('female')]}
                                            value={gender}
                                            onChange={handleGenderChange}
                                            size="medium"
                                            sx={{
                                                width: '100%',
                                                height: '40px',
                                            }}
                                        />
                                    </Box>

                                    {/* 소속기관코드 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '10px',
                                            alignSelf: 'stretch',
                                            paddingLeft: '15px',
                                            paddingBottom: '10px',
                                        }}
                                    >
                                        <Box
                                            sx={commonLabelContainerSx}
                                        >
                                            <FormLabel
                                                text={t('affiliated_organization')}
                                                sx={commonLabelTextSx}
                                            />
                                        </Box>
                                        {organizationId && (
                                            <Box sx={{
                                                display: 'flex',
                                                padding: '0 15px',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flex: '1 0 0',
                                                alignSelf: 'stretch',
                                            }}
                                            >
                                                <Typography
                                                    sx={{
                                                        flex: 1,
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        letterSpacing: '-0.42px',
                                                        textAlign: 'left',
                                                    }}
                                                >
                                                    {selectedOrganization?.name}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Button
                                            size="small"
                                            narrow
                                            onClick={() => {
                                                navigate('/auth/organization-search', {
                                                    state: {
                                                        source: 'signup',
                                                        formData: {
                                                            email,
                                                            auth,
                                                            // 입력한 비밀번호는 항상 포함 (보존)
                                                            password,
                                                            passwordConfirm,
                                                            userName,
                                                            birthDate,
                                                            gender,
                                                            step,
                                                            agree,
                                                            organizationId,
                                                            selectedOrganization,
                                                            division,
                                                            section
                                                        }
                                                    }
                                                });
                                            }}
                                        >
                                            {organizationId ? t('search_again') : t('search')}
                                        </Button>
                                    </Box>
                                    {organizationId && <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            width: '320px',
                                            height: '30px',
                                            paddingLeft: '15px',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            gap: '10px',
                                        }}>
                                            <Typography sx={{
                                                minWidth: '100px',
                                                color: theme.palette.figma.white[60],
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                lineHeight: 'normal',
                                                letterSpacing: '-0.42px',
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap',
                                            }}>{t('division_1')}</Typography>
                                            <Box sx={{
                                                display: 'flex',
                                                height: '40px',
                                                padding: '0 15px',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flex: '1 0 0',
                                            }}><Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    fontWeight: 400,
                                                }}
                                            >
                                                    {division}
                                                </Typography></Box>

                                        </Box>
                                        {section && <Box sx={{
                                            display: 'flex',
                                            width: '320px',
                                            height: '30px',
                                            paddingLeft: '15px',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            gap: '10px',
                                        }}>
                                            <Typography sx={{
                                                minWidth: '100px',
                                                color: theme.palette.figma.white[60],
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                lineHeight: 'normal',
                                                letterSpacing: '-0.42px',
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap',
                                            }}>{t('division_2')}</Typography>
                                            <Box sx={{
                                                display: 'flex',
                                                height: '40px',
                                                padding: '0 15px',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flex: '1 0 0',
                                            }}><Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    fontWeight: 400,
                                                }}
                                            >
                                                    {section}
                                                </Typography></Box>

                                        </Box>}
                                    </Box>
                                    }
                                </Box>

                            </Box>
                        </Box>
                    </Box>

                    {/* 하단 버튼 섹션 */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: '20px',
                            alignSelf: 'stretch',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                padding: '0px 0px',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '20px',
                                alignSelf: 'stretch',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    alignSelf: 'stretch',
                                }}
                            >
                                <Checkbox
                                    checked={agree}
                                    onChange={checked => setAgree(checked)}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '2px',
                                        flex: '1 0 0',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <StyledLinkTypography
                                        onClick={() => {
                                            navigate('/auth/terms', { state: buildTermsNavigationState(type[0]) });
                                        }}
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            letterSpacing: '-0.42px',
                                        }}
                                    >
                                        "{t('terms_of_service')}"
                                    </StyledLinkTypography>
                                    <StyledTypography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            letterSpacing: '-0.42px',
                                        }}
                                    >
                                        {t('and')}&nbsp;
                                    </StyledTypography>
                                    <StyledLinkTypography
                                        onClick={() => {
                                            navigate('/auth/terms', { state: buildTermsNavigationState(type[1]) });
                                        }}
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            letterSpacing: '-0.42px',
                                        }}
                                    >
                                        "{t('privacy_policy')}"
                                    </StyledLinkTypography>
                                    <StyledTypography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            letterSpacing: '-0.42px',
                                        }}
                                    >
                                        {t('i_agree')}
                                    </StyledTypography>
                                </Box>
                            </Box>
                            <Button
                                variant={
                                    (isSnsSignUpForRender
                                        ? true
                                        : password.length >= 6 &&
                                        password === passwordConfirm) &&
                                        (!isPasswordOnlyFlow
                                            ? userName &&
                                            birthDate &&
                                            gender >= 0
                                            : true) &&
                                        agree
                                        ? 'normal'
                                        : 'gray'
                                }
                                disabled={
                                    // SNS 가입은 비밀번호 입력 없이 가입
                                    (isSnsSignUpForRender
                                        ? false
                                        : password.length < 6 ||
                                        password !== passwordConfirm ||
                                        passwordError ||
                                        passwordConfirmError) ||
                                    (!isPasswordOnlyFlow &&
                                        (userNameError ||
                                            birthDateError ||
                                            !userName ||
                                            !birthDate ||
                                            gender < 0)) ||
                                    !agree
                                }
                                onClick={handleRegister}
                                sx={{
                                    alignSelf: 'stretch',
                                    height: '60px',
                                    minHeight: '60px',
                                    maxHeight: '60px',
                                }}
                                size="large"
                            >
                                {t('confirm')}
                            </Button>
                        </Box>
                    </Box>
                </>
            )}

            <Popup
                open={popupOpen}
                message={popupMessage}
                subMessage={popupSubMessage}
                onConfirm={handleClosePopup}
            />
        </StyledContainer>
    );
};
